import { useEffect, useRef, useState } from "react";
import { Box, Button } from "@mui/material";
import AppModal from "global/components/AppModal/AppModal";
import strings from "global/constants/StringConstants";
import { maskedContent } from "helpers/maskedContent";
import history from "utils/history";
import { ContractDocument } from "screens/ContractSearch/ContractSearchTypes";
import { LibraryStatus } from "../useContractLibrary";
import styles from "../ContractCompare.styles";
import t from "../ContractCompare.tokens";
import {
  AlertCircleGlyph,
  CardGlyph,
  CloseGlyph,
  InfoGlyph,
  SearchGlyph,
  UploadGlyph,
} from "./ContractCompareIcons";
import KindTile from "./KindTile";
import LibraryList from "./LibraryList";
import UploadZone from "./UploadZone";

export type OldTab = "library" | "upload";

export interface OldPick {
  documentId: string;
  fileName: string;
}

interface OldVersionPickerProps {
  tab: OldTab;
  onTab: (tab: OldTab) => void;
  library: { status: LibraryStatus; contracts: ContractDocument[]; retry: () => void };
  pick: OldPick | null;
  /** The pick came from Contract Search: the picker opens on a "Picked from Contract Search" line. */
  pickFromSearch: boolean;
  onPick: (document: ContractDocument) => void;
  /** Words of the server about the pick (a 404): shown under the choice. */
  pickError: string | null;
  upload: { file: File | null; problem: string | null };
  onUploadFile: (file: File, problem: string | null) => void;
  onUploadRemove: () => void;
  disabled: boolean;
  /** Why the page is blocked, for a click on a disabled upload zone. */
  disabledMessage?: string;
  isPhone: boolean;
  /** Runs the page's own history push without the leave guard counting it (the phone sheet). */
  bypass: <T>(run: () => T) => T;
}

const page = strings.contractCompare.page;

/** The "Picked from Contract Search" line, which names the picked-contract group (one banner on the page). */
const PICKED_LABEL_ID = "compare-picked-label";

/** Step 1: the old version, from the user's own contracts or an upload. Only the active tab's choice is sent. */
const OldVersionPicker = ({
  tab,
  onTab,
  library,
  pick,
  pickFromSearch,
  onPick,
  pickError,
  upload,
  onUploadFile,
  onUploadRemove,
  disabled,
  disabledMessage,
  isPhone,
  bypass,
}: OldVersionPickerProps) => {
  const [query, setQuery] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetPick, setSheetPick] = useState<ContractDocument | null>(null);
  // The Contract Search line stays until the user asks to change; then the list shows with the pick kept.
  const [showList, setShowList] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  // A contract chosen in the sheet is applied once the sheet's own history entry is gone, so the page's router-state
  // update (D50) lands on the page's entry and not on the sheet's.
  const pendingPick = useRef<ContractDocument | null>(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  // A refresh on the sheet's entry (or a phone browser reloading a discarded tab) keeps its `compareSheet` flag on the
  // page's own entry. Left there, the next sheet's Back would meet a flagged entry and not close it, so the first tap
  // on Close, Esc or "Use this contract" would do nothing (D49, D59). The flag goes on mount; the rest of the state stays.
  useEffect(() => {
    const carried = history.location.state as ({ compareSheet?: boolean } & Record<string, unknown>) | undefined;
    if (!carried?.compareSheet) return;
    const { compareSheet, ...rest } = carried;
    bypass(() =>
      history.replace(
        { pathname: history.location.pathname, search: history.location.search },
        Object.keys(rest).length ? rest : undefined,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phone Back closes the sheet: the sheet pushed one history entry (same path and query, only the state differs).
  useEffect(() => {
    if (!sheetOpen) return undefined;
    return history.listen((location, action) => {
      const flagged = (location.state as { compareSheet?: boolean } | undefined)?.compareSheet;
      if (action !== "POP" || flagged) return;
      setSheetOpen(false);
      const chosen = pendingPick.current;
      pendingPick.current = null;
      if (chosen) onPickRef.current(chosen);
    });
  }, [sheetOpen]);

  // A sheet left open when the page is no longer on a phone, or the tab changed, closes the same way Back does, so
  // its history entry goes with it.
  useEffect(() => {
    if (sheetOpen && (!isPhone || tab !== "library")) closeSheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhone, tab, sheetOpen]);

  const openSheet = () => {
    setSheetPick(null);
    bypass(() =>
      history.push(
        { pathname: history.location.pathname, search: history.location.search },
        { ...(history.location.state as object | undefined), compareSheet: true },
      ),
    );
    setSheetOpen(true);
  };

  /** Pops the sheet's own entry; the listener above then closes the sheet (the leave guard lets a Back to the same page through). */
  const closeSheet = () => {
    bypass(() => history.goBack());
  };

  const useSheetPick = () => {
    pendingPick.current = sheetPick;
    closeSheet();
  };

  const hasContracts = library.status === "ready" && library.contracts.length > 0;
  const listHidden = pickFromSearch && !!pick && !showList && tab === "library";

  const searchBox = (
    <Box sx={styles.searchBox(searchFocused)}>
      <SearchGlyph sx={{ width: t.search.icon, height: t.search.icon, flexShrink: 0 }} />
      {/* What the user types is usually part of a confidential name: masked in the box itself, not only when echoed. */}
      <Box
        component="input"
        type="search"
        {...maskedContent}
        value={query}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
        placeholder={page.searchLabel}
        aria-label={page.searchLabel}
        disabled={disabled}
        onFocus={() => setSearchFocused(true)}
        onBlur={() => setSearchFocused(false)}
        sx={styles.searchInput}
      />
      {query && (
        <Box
          component="button"
          type="button"
          sx={styles.iconButton}
          onClick={() => setQuery("")}
          aria-label={page.clearSearchBox}
        >
          <CloseGlyph sx={{ width: 16, height: 16 }} />
        </Box>
      )}
    </Box>
  );

  const hint = (
    <Box component="p" sx={styles.hint}>
      <InfoGlyph sx={{ width: t.hint.icon, height: t.hint.icon, flexShrink: 0, mt: "1px" }} />
      {page.libraryHint}
    </Box>
  );

  const list = (inSheet: boolean) => (
    <LibraryList
      status={library.status}
      contracts={library.contracts}
      query={query}
      selectedId={inSheet ? (sheetPick?.documentId ?? pick?.documentId ?? null) : (pick?.documentId ?? null)}
      pending={!inSheet && pick && library.status === "loading" ? pick : null}
      onSelect={inSheet ? setSheetPick : onPick}
      onRetry={library.retry}
      onClearQuery={() => setQuery("")}
      name={inSheet ? "compare-old-sheet" : "compare-old"}
      inSheet={inSheet}
      disabled={disabled}
    />
  );

  const libraryPanel = () => {
    if (listHidden && pick) {
      return (
        <Box sx={styles.pickedBanner} role="group" aria-labelledby={PICKED_LABEL_ID} {...maskedContent}>
          <KindTile fileName={pick.fileName} />
          <Box sx={styles.rowText}>
            <Box component="span" id={PICKED_LABEL_ID} sx={styles.eyebrow}>
              {page.pickedFromSearch}
            </Box>
            <Box component="span" dir="auto" sx={styles.fileName}>
              {pick.fileName}
            </Box>
          </Box>
          <Button variant="outlined" size="small" onClick={() => setShowList(true)} disabled={disabled} sx={styles.changeButton}>
            {page.change}
          </Button>
        </Box>
      );
    }
    if (isPhone) {
      return (
        <>
          {pick && (
            <Box sx={styles.pickedBanner} {...maskedContent}>
              <KindTile fileName={pick.fileName} />
              <Box component="span" dir="auto" sx={styles.fileName}>
                {pick.fileName}
              </Box>
            </Box>
          )}
          <Button variant="outlined" onClick={openSheet} disabled={disabled} fullWidth sx={styles.actionButton}>
            <CardGlyph sx={{ width: 18, height: 18, mr: 1 }} />
            {page.chooseFromMine}
          </Button>
        </>
      );
    }
    return (
      <>
        {searchBox}
        {hint}
        {list(false)}
      </>
    );
  };

  const uploadPanel = () => (
    <>
      {library.status === "ready" && !hasContracts && (
        <Box component="p" sx={styles.hint}>
          <InfoGlyph sx={{ width: t.hint.icon, height: t.hint.icon, flexShrink: 0, mt: "1px" }} />
          {page.libraryEmpty}
        </Box>
      )}
      <UploadZone
        side="old"
        file={upload.file}
        problem={upload.problem}
        onFile={onUploadFile}
        onRemove={onUploadRemove}
        disabled={disabled}
        disabledMessage={disabledMessage}
        compact
      />
      {hasContracts && !upload.file && (
        <Box component="button" type="button" sx={styles.linkButton} onClick={() => onTab("library")}>
          {page.pickAnother}
        </Box>
      )}
    </>
  );

  /** The tabs pattern: arrow keys (and Home / End) move between the two tabs; only the selected one is in the tab order. */
  const onTabKeys = (event: React.KeyboardEvent<HTMLElement>) => {
    const order: OldTab[] = ["library", "upload"];
    const index = order.indexOf(tab);
    let next = index;
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") next = (index + 1) % order.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = order.length - 1;
    else return;
    event.preventDefault();
    onTab(order[next]);
    document.getElementById(`compare-old-tab-${order[next]}`)?.focus();
  };

  const tabButton = (id: OldTab, label: string, icon: JSX.Element) => (
    <Box
      component="button"
      type="button"
      role="tab"
      id={`compare-old-tab-${id}`}
      aria-selected={tab === id}
      aria-controls="compare-old-panel"
      tabIndex={tab === id ? 0 : -1}
      sx={styles.tab(tab === id)}
      onClick={() => onTab(id)}
      disabled={disabled}
    >
      {icon}
      {label}
    </Box>
  );

  return (
    <Box component="section" sx={styles.stepCard} aria-labelledby="compare-old-title">
      <Box sx={styles.stepHead}>
        <Box sx={styles.stepBadge} aria-hidden="true">
          1
        </Box>
        <Box>
          <Box component="h2" id="compare-old-title" tabIndex={-1} sx={styles.stepTitle}>
            {page.stepOldTitle}
          </Box>
          <Box component="p" sx={styles.stepHint}>
            {page.stepOldHint}
          </Box>
        </Box>
      </Box>

      <Box role="tablist" aria-label={page.tabsLabel} sx={styles.tabs} onKeyDown={onTabKeys}>
        {tabButton("library", page.tabLibrary, <CardGlyph sx={{ width: t.tabs.icon, height: t.tabs.icon }} />)}
        {tabButton("upload", page.tabUpload, <UploadGlyph sx={{ width: t.tabs.icon, height: t.tabs.icon }} />)}
      </Box>

      <Box
        role="tabpanel"
        id="compare-old-panel"
        aria-labelledby={`compare-old-tab-${tab}`}
        sx={{ display: "flex", flexDirection: "column", gap: "14px", minWidth: 0 }}
      >
        {tab === "library" ? libraryPanel() : uploadPanel()}
      </Box>

      {pickError && tab === "library" && (
        <Box component="p" role="alert" sx={styles.fieldError} {...maskedContent}>
          <AlertCircleGlyph sx={{ width: 16, height: 16, mt: "1px", flexShrink: 0 }} />
          {pickError}
        </Box>
      )}

      <AppModal
        open={sheetOpen}
        onClose={closeSheet}
        title={page.chooseOld}
        hideIcon
        fullScreenOnMobile
        size="md"
        primaryLabel={page.useThis}
        onPrimary={useSheetPick}
        primaryDisabled={!sheetPick}
        hideCancel
        scrollBody={false}
      >
        <Box sx={styles.sheetBody}>
          {searchBox}
          {hint}
          {list(true)}
        </Box>
      </AppModal>
    </Box>
  );
};

export default OldVersionPicker;
